import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
// import CardListHeading from "@insite/content-library/Components/CardListHeading";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CardListHeading from "@MasterBluePrint/Overrides/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@MasterBluePrint/Overrides/Components/QuoteSummaryCard";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    noQuotesDate?: InjectableCss;
    RecentQuotesWrapper?: InjectableCss;
    quotesViewAllLink?: InjectableCss;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;

            @media screen and (min-width: 768px) {
                margin: 0 10px !important;
            }

            @media screen and (min-width: 767px) and (max-width: 990px) {
                padding-left: 10px;
                padding-right: 10px;
            }
        `,
    },
    noQuotesDate: {
        css: css`
            color: #002554;
            font-size: 0.875rem;
            letter-spacing: 0;
            line-height: 1rem;
            padding: 1.375rem 0.75rem 0.625rem 0.75rem;
            border-radius: 0 0 2px 2px;
            background-color: #E5EEF7;
            width: 100%;
        `,
    },
    noQuotesText: {
        css: css`
            color: #2E384D;
            font-family: 'Lato',sans-serif;
            font-size: 0.875rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1rem;
            padding: 1.25rem 1.563rem;
            background-color: #fff;
            width: 100%;
        `,
    },
    RecentQuotesWrapper: {
        css: css`
            padding-bottom: 1.875rem;

            div.card_container__wrapper {
                background-color: #f7f9fa !important;
                box-shadow: none !important;
                border: none !important;

                > div {
                    padding-top: 0 !important;
                }

                @media screen and (min-width: 767px) and (max-width: 990px) {
                    > div {
                        padding-left: 10px;
                        padding-right: 10px;
                    }
                }

                @media screen and (max-width: 767px) {
                    > div {
                        padding-left: 0.625rem;
                        padding-right: 0.625rem;
                    }
                }
            }
        `,
    },
    quotesViewAllLink: {
        css: css`
            margin-bottom: 1.875rem;
            width: 70%;
            position: relative;
            text-align: center;
            margin: 0 auto;
            text-transform: none;
            
            &:after {
                border-top: 1px solid #E4E8F0;
                content: '';
                display: block;
                position: absolute;
                top: 18px;
                width: 100%;
            }
            
            span {
                color: #002554 !important;
                font-family: 'Roboto', sans-serif;
                text-transform: none;
                font-size: 0.875rem;
                font-weight: 500;
                letter-spacing: 1.31px;
                line-height: 1rem;
                text-align: center;
                padding: 0.625rem 2.5rem;
                background: #f7f9fa;
                position: relative;
                z-index: 1;
            }

            .disable-look {
                cursor: default;
                pointer-events: none;
                span {
                    color: #CBCBCB !important;
                }
            }
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes, myAccountPageUrl } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;
        return (
            <CardList
                extendedStyles={styles.cardList}
                {...styles.RecentQuotesWrapper}
            >
                <CardListHeading heading={translate("Recent Quote Requests")} viewAllUrl={myQuotesPageUrl} length={quotesDataView.value.length} />
                {quotesDataView.value.length === 0 && (
                    <GridItem {...styles.noQuotesGridItem}>
                        <Typography as="div" {...styles.noQuotesDate}>{translate("Date Requested")}</Typography>
                        <Typography as="div" {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                    </GridItem>
                )}
                {quotesDataView.value.map(quote => (
                    <CardContainer key={quote.id} extendedStyles={styles.cardContainer}>
                        <QuoteSummaryCard
                            quote={quote}
                            session={session}
                            quoteSettings={settingsCollection.quoteSettings}
                            extendedStyles={styles.quoteSummaryCard}
                        />
                    </CardContainer>
                ))}
                {myAccountPageUrl ?
                    <Typography as="div" {...styles.quotesViewAllLink}>
                        <Link
                            href={myQuotesPageUrl}
                            typographyProps={{ size: 14 }}
                            data-test-selector="recentQuotesViewAllLink"
                            className={`${quotesDataView.value.length === 0 ? 'disable-look' : ''}`}
                        >
                            {translate("View all ")}
                        </Link>
                    </Typography>
                    : ''}
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
